import { zonedDayKey } from '../utils/kernelTimezone';
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  getDailyReport,
  getWeeklyReport,
  downloadReportExcel,
  getReportSubscriptions,
  saveReportSubscription,
  sendReportNow,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import '../components/kernelSwitch.css';
import { fmtDateTime, fmtDateDMY } from '../utils/format';
import { PageHeading, Pill, NtIcon, Th, STATUS_COLORS } from '../components/NotionUI';
import Select from '../components/Select';
import { Link } from 'react-router-dom';

const todayIso = () => {
  return zonedDayKey(new Date());
};

const SUMMARY_KEYS = ['tickets_created', 'tickets_resolved_or_closed', 'replies_to_customers', 'internal_notes', 'customer_messages', 'open_tickets_now', 'overdue_now', 'due_today'];

function TicketLine({ ticket, from }) {
  const { t } = useLanguage();
  return (
    <li className="rp-line">
      <Link to={`/tickets/${ticket.id}`} state={{ from }} className="rp-link">
        <span className="rp-id">#{ticket.id}</span>
        <span className="rp-subject">{ticket.subject}</span>
      </Link>
      {ticket.requester && <span className="nt-muted rp-req">{ticket.requester}</span>}
      <Pill dot color={STATUS_COLORS[ticket.status]}>{t(`tickets.status_${ticket.status}`)}</Pill>
    </li>
  );
}

function ListBlock({ title, tickets, from, extra }) {
  if (!tickets || tickets.length === 0) return null;
  return (
    <section className="rp-block">
      <h4>{title} <span className="nt-muted">({tickets.length})</span></h4>
      <ul className="rp-list">
        {tickets.map((tk) => (
          <li key={tk.id} className="rp-li">
            <TicketLine ticket={tk} from={from} />
            {extra && <span className="nt-muted rp-extra">{extra(tk)}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

function SubscriptionCard({ kind, sub, email, onSaved }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(sub);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => setForm(sub), [sub]);

  const save = async (next) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await saveReportSubscription(kind, next);
      setMessage(t('reports.saved'));
      onSaved();
    } catch (err) {
      setError(errorMessage(err, t('reports.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setError('');
    setMessage('');
    try {
      const res = await sendReportNow(kind);
      setMessage(t('reports.sentTo', { email: res.to }));
    } catch (err) {
      setError(errorMessage(err, t('reports.sendError')));
    }
  };

  const hours = Array.from({ length: 24 }, (_, h) => h);

  return (
    <div className="rp-sub">
      <div className="rp-sub-head">
        <button
          type="button"
          role="switch"
          aria-checked={form.is_active}
          className={'au-switch' + (form.is_active ? ' au-switch-on' : '')}
          onClick={() => {
            const next = { ...form, is_active: !form.is_active };
            setForm(next);
            save(next);
          }}
          aria-label={t(`reports.sub_${kind}`)}
        >
          <span className="au-switch-knob" />
        </button>
        <strong>{t(`reports.sub_${kind}`)}</strong>
      </div>
      <p className="tp-hint">{t(`reports.subHint_${kind}`, { email })}</p>
      <div className="rp-sub-fields">
        {kind === 'weekly' && (
          <div className="form-group">
            <label>{t('reports.sendDay')}</label>
            <Select value={String(form.weekday)} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}>
              {WEEKDAYS.map((d) => <option key={d} value={d}>{t(`reports.day_${d}`)}</option>)}
            </Select>
          </div>
        )}
        <div className="form-group">
          <label>{t('reports.sendHour')}</label>
          <Select value={String(form.send_hour)} onChange={(e) => setForm({ ...form, send_hour: Number(e.target.value) })}>
            {hours.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </Select>
        </div>
        {kind === 'weekly' && (
          <div className="form-group">
            <label>{t('reports.groupBy')}</label>
            <Select value={form.group_by} onChange={(e) => setForm({ ...form, group_by: e.target.value })}>
              <option value="customer">{t('reports.byCustomer')}</option>
              <option value="project">{t('reports.byProject')}</option>
            </Select>
          </div>
        )}
      </div>
      {kind === 'daily' && <p className="tp-hint">{form.send_hour < 12 ? t('reports.dailyCoversYesterday') : t('reports.dailyCoversToday')}</p>}
      {kind === 'weekly' && <p className="tp-hint">{t('reports.weeklyCoversLast')}</p>}
      <label className="ksw-row"><span className="ksw"><input type="checkbox" checked={form.only_mine} onChange={(e) => setForm({ ...form, only_mine: e.target.checked })} /><span className="ksw-slider" /></span><span>{t('reports.onlyMine')}</span></label>
      <label className="ksw-row"><span className="ksw"><input type="checkbox" checked={form.include_excel} onChange={(e) => setForm({ ...form, include_excel: e.target.checked })} /><span className="ksw-slider" /></span><span>{t('reports.includeExcel')}</span></label>
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      <div className="rp-sub-actions">
        <button type="button" className="btn btn-sm rp-btn" disabled={saving} onClick={() => save(form)}><NtIcon name="save" size={15} /> {saving ? t('common.saving') : t('common.save')}</button>
        <button type="button" className="rp-action" onClick={test}><NtIcon name="send" size={15} /> {t('reports.sendNow')}</button>
      </div>
    </div>
  );
}

function Reports({ user, onLogout }) {
  const { t } = useLanguage();
  const [kind, setKind] = useState('daily');
  const [date, setDate] = useState(todayIso());
  const [groupBy, setGroupBy] = useState('customer');
  const [onlyMine, setOnlyMine] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subs, setSubs] = useState(null);
  const [subsEmail, setSubsEmail] = useState('');
  const [open, setOpen] = useState({});

  const params = useCallback(() => (kind === 'daily'
    ? { date, only_mine: onlyMine ? 1 : undefined }
    : { week_start: date, group_by: groupBy, only_mine: onlyMine ? 1 : undefined }), [kind, date, groupBy, onlyMine]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setReport(kind === 'daily' ? await getDailyReport(params()) : await getWeeklyReport(params()));
    } catch (err) {
      setError(errorMessage(err, t('reports.loadError')));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [kind, params, t]);

  const loadSubs = useCallback(async () => {
    try {
      const res = await getReportSubscriptions();
      setSubs(res.subscriptions);
      setSubsEmail(res.email);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSubs(); }, [loadSubs]);

  const exportExcel = async () => {
    try {
      await downloadReportExcel({ type: kind, ...params() });
    } catch (err) {
      setError(errorMessage(err, t('reports.exportError')));
    }
  };

  const summary = report?.summary || report?.totals;
  const from = '/reports';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page rp-page">
        <PageHeading icon="chart" title={t('reports.title')} subtitle={t('reports.subtitle')} />

        <div className="tp-toolbar rp-noprint">
          <div className="tpl-cats" role="group" aria-label={t('reports.kind')}>
            <button type="button" className={'tpl-cat' + (kind === 'daily' ? ' tpl-cat-active' : '')} onClick={() => setKind('daily')}>{t('reports.daily')}</button>
            <button type="button" className={'tpl-cat' + (kind === 'weekly' ? ' tpl-cat-active' : '')} onClick={() => setKind('weekly')}>{t('reports.weekly')}</button>
          </div>
          <label className="nt-filter">
            <NtIcon name="calendar" />
            <span>{kind === 'daily' ? t('reports.day') : t('reports.weekOf')}</span>
            <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </label>
          {kind === 'weekly' && (
            <label className="nt-filter">
              <NtIcon name="users" />
              <span>{t('reports.groupBy')}</span>
              <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                <option value="customer">{t('reports.byCustomer')}</option>
                <option value="project">{t('reports.byProject')}</option>
              </Select>
            </label>
          )}
          <label className="nt-filter">
            <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
            {t('reports.onlyMineShort')}
          </label>
          <span className="tp-spacer" />
          <button type="button" className="rp-action" onClick={exportExcel} disabled={!report}><NtIcon name="download" size={15} /> {t('reports.excel')}</button>
          <button type="button" className="rp-action" onClick={() => window.print()} disabled={!report}><NtIcon name="printer" size={15} /> {t('reports.print')}</button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {loading && <div className="loading">{t('common.loading')}</div>}

        {report && !loading && (
          <div className="rp-report">
            <h3 className="rp-title">
              {report.report === 'daily'
                ? t('reports.titleDaily', { date: fmtDateDMY(report.date) })
                : t('reports.titleWeekly', { from: fmtDateDMY(report.week_start), to: fmtDateDMY(report.week_end) })}
            </h3>
            <div className="rp-tiles">
              {(report.report === 'daily' ? SUMMARY_KEYS : ['tickets', 'created', 'resolved_or_closed', 'replies_to_customers', 'customer_messages']).map((key) => (
                <div key={key} className="rp-tile">
                  <div className="rp-tile-value">{summary[key] ?? 0}</div>
                  <div className="rp-tile-label">{t(`reports.k_${key}`)}</div>
                </div>
              ))}
            </div>

            {report.report === 'daily' ? (
              <>
                <ListBlock title={t('reports.sec_created')} tickets={report.tickets_created} from={from} />
                <ListBlock title={t('reports.sec_closed')} tickets={report.tickets_resolved_or_closed} from={from} />
                <ListBlock
                  title={t('reports.sec_conversations')}
                  tickets={report.conversations}
                  from={from}
                  extra={(c) => t('reports.convExtra', { replies: c.replies_to_customer, notes: c.internal_notes, customer: c.customer_messages })}
                />
                <ListBlock title={t('reports.sec_overdue')} tickets={report.overdue} from={from} extra={(c) => fmtDateDMY(c.due_at)} />
                <ListBlock title={t('reports.sec_dueToday')} tickets={report.due_today} from={from} />
              </>
            ) : (
              <>
                {report.groups.length === 0 && <p className="nt-empty">{t('reports.noActivity')}</p>}
                {report.groups.length > 0 && (
                  <div className="nt-table-wrap">
                    <table className="nt-table cu-table">
                      <thead>
                        <tr>
                          <Th icon={report.group_by === 'customer' ? 'user' : 'tag'}>{report.group_by === 'customer' ? t('reports.byCustomer') : t('reports.byProject')}</Th>
                          <Th icon="hash">{t('reports.k_created')}</Th>
                          <Th icon="hash">{t('reports.k_resolved_or_closed')}</Th>
                          <Th icon="hash">{t('reports.k_still_open')}</Th>
                          <Th icon="hash">{t('reports.k_replies_to_customers')}</Th>
                          <Th icon="hash">{t('reports.k_customer_messages')}</Th>
                          <Th icon="clock">{t('reports.k_avg_hours')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.groups.map((g) => (
                          <React.Fragment key={g.label}>
                            <tr className="nt-clickable" onClick={() => setOpen({ ...open, [g.label]: !open[g.label] })}>
                              <td>
                                <strong>{g.label}</strong>
                                {g.customer_group && <span className="nt-muted"> · {g.customer_group}</span>}
                              </td>
                              <td>{g.created}</td>
                              <td>{g.resolved_or_closed}</td>
                              <td>{g.still_open}</td>
                              <td>{g.replies_to_customers}</td>
                              <td>{g.customer_messages}</td>
                              <td>{g.avg_resolution_hours ?? '—'}</td>
                            </tr>
                            {(
                              <tr className={'rp-detail-row' + (open[g.label] ? ' rp-open' : '')}>
                                <td colSpan={7}>
                                  <ul className="rp-list">
                                    {g.tickets.map((tk) => (
                                      <li key={tk.id} className="rp-li">
                                        <TicketLine ticket={tk} from={from} />
                                        <span className="nt-muted rp-extra">{fmtDateTime(tk.created_at)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                  {g.more_tickets > 0 && <p className="nt-muted">{t('reports.moreTickets', { count: g.more_tickets })}</p>}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {report.groups.length > 0 && <p className="tp-hint">{t('reports.stillOpenNote')}</p>}
              </>
            )}

            {report.project_activity && (report.project_activity.tasks_created > 0 || report.project_activity.tasks_updated > 0) && (
              <section className="rp-block">
                <h4>{t('reports.sec_projects')} <span className="nt-muted">({t('reports.projectsCount', { created: report.project_activity.tasks_created, updated: report.project_activity.tasks_updated })})</span></h4>
                <ul className="rp-list">
                  {report.project_activity.items.map((it) => (
                    <li key={`${it.task_id}-${it.change}`} className="rp-li">
                      <span className="rp-subject">{it.project}: {it.title}</span>
                      <span className="nt-muted rp-extra">{it.change === 'created' ? t('reports.created') : t('reports.updated')}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {report.meetings && report.meetings.length > 0 && (
              <section className="rp-block">
                <h4>{t('reports.sec_meetings')}</h4>
                <ul className="rp-list">
                  {report.meetings.map((m) => (
                    <li key={m.id} className="rp-li"><Link to={`/meetings/${m.id}`} className="rp-link">{m.title}</Link>{m.date && <span className="nt-muted rp-extra">{fmtDateTime(m.date)}</span>}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        {subs && (
          <section className="rp-subs rp-noprint">
            <h3 className="cl-card-title">{t('reports.emailTitle')}</h3>
            <p className="tp-hint">{t('reports.emailHint')}</p>
            <div className="rp-subs-grid">
              <SubscriptionCard kind="daily" sub={subs.daily} email={subsEmail} onSaved={loadSubs} />
              <SubscriptionCard kind="weekly" sub={subs.weekly} email={subsEmail} onSaved={loadSubs} />
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}

export default Reports;
